import { QueryClient, DefaultOptions } from '@tanstack/react-query';

const queryConfig: DefaultOptions = {
  queries: {
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
  mutations: {
    retry: false,
  },
};

export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

export const QUERY_KEYS = {
  AUTH: {
    ME: ['auth', 'me'] as const,
  },
  DEPOSITS: {
    ALL: ['deposits'] as const,
    HISTORY: (params?: Record<string, unknown>) => 
      ['deposits', 'history', params] as const,
    STATUS: (address: string) => 
      ['deposits', 'status', address] as const,
  },
  WITHDRAWALS: {
    ALL: ['withdrawals'] as const,
    HISTORY: (params?: Record<string, unknown>) => 
      ['withdrawals', 'history', params] as const,
    DETAIL: (id: number | string) => 
      ['withdrawals', 'detail', id] as const,
  },
  ADMIN: {
    USERS: {
      ALL: ['admin', 'users'] as const,
      LIST: (params?: Record<string, unknown>) => 
        ['admin', 'users', 'list', params] as const,
      DETAIL: (id: number | string) => 
        ['admin', 'users', 'detail', id] as const,
    },
    TRANSACTIONS: {
      ALL: ['admin', 'transactions'] as const,
      LIST: (params?: Record<string, unknown>) =>
        ['admin', 'transactions', 'list', params] as const,
      DETAIL: (id: number | string) =>
        ['admin', 'transactions', 'detail', id] as const,
      RETURN_INFO: (id: number | string) =>
        ['admin', 'transactions', 'return-info', id] as const,
    },
    WALLETS: {
      ALL: ['admin', 'wallets'] as const,
      HOT_WALLET: ['admin', 'wallets', 'hot-wallet'] as const,
      HOT_WALLET_TRANSACTIONS: (params?: Record<string, unknown>) =>
        ['admin', 'wallets', 'hot-wallet', 'transactions', params] as const,
      POOL: ['admin', 'wallets', 'pool'] as const,
      BALANCES: ['admin', 'wallets', 'balances'] as const,
      DELETED: {
        ALL: ['admin', 'wallets', 'deleted'] as const,
        LIST: (params?: Record<string, unknown>) =>
          ['admin', 'wallets', 'deleted', 'list', params] as const,
      },
      CLIENT_STATIC: {
        ALL: ['admin', 'wallets', 'client-static'] as const,
        LIST: (params?: Record<string, unknown>) =>
          ['admin', 'wallets', 'client-static', 'list', params] as const,
        DETAIL: (id: number | string) =>
          ['admin', 'wallets', 'client-static', 'detail', id] as const,
        TRANSACTIONS: (id: number | string, params?: Record<string, unknown>) =>
          ['admin', 'wallets', 'client-static', 'detail', id, 'transactions', params] as const,
      },
      POOL_COLLECT_STATUS: ['admin', 'wallets', 'pool', 'collect', 'status'] as const,
      RETURNED_WITHDRAW: {
        PREVIEW: ['admin', 'wallets', 'deleted', 'returned', 'preview'] as const,
        STATUS: ['admin', 'wallets', 'deleted', 'returned', 'status'] as const,
      },
      CLIENT_STATIC_WITHDRAW: {
        PREVIEW: ['admin', 'wallets', 'client-static', 'banned', 'preview'] as const,
        STATUS: ['admin', 'wallets', 'client-static', 'banned', 'status'] as const,
      },
    },
    STATISTICS: {
      ALL: ['admin', 'statistics'] as const,
    },
    ACTIVITY_LOGS: {
      ALL: ['admin', 'activity-logs'] as const,
      LIST: (params?: Record<string, unknown>) =>
        ['admin', 'activity-logs', 'list', params] as const,
    },
    BALANCE_LOGS: {
      ALL: ['admin', 'balance-logs'] as const,
      LIST: (params?: Record<string, unknown>) =>
        ['admin', 'balance-logs', 'list', params] as const,
      COMMISSION_SUMMARY: (params?: Record<string, unknown>) =>
        ['admin', 'balance-logs', 'commission-summary', params] as const,
      SYSTEM_MOVEMENTS: (params?: Record<string, unknown>) =>
        ['admin', 'balance-logs', 'system-movements', params] as const,
    },
    WEBHOOK_DELIVERIES: {
      ALL: ['admin', 'webhook-deliveries'] as const,
      LIST: (params?: Record<string, unknown>) =>
        ['admin', 'webhook-deliveries', 'list', params] as const,
      DETAIL: (id: number | string) =>
        ['admin', 'webhook-deliveries', 'detail', id] as const,
    },
    SETTINGS: {
      ALL: ['admin', 'settings'] as const,
      DETAIL: (key: string) => 
        ['admin', 'settings', 'detail', key] as const,
    },
    HEALTH: {
      STATUS: ['admin', 'health', 'status'] as const,
    },
  },
} as const;
